/**
 * E-Mail-Vorlagen für die Werkzeugleiste (ComposeTools): Liste, Kategorien
 * und – für Administratoren – die Unternehmen zum Anlegen geteilter Vorlagen.
 * Geladen wird beim ersten Öffnen des Vorlagen-Fensters.
 */
import { ref } from 'vue'
import { getRequestGeneric } from '@/services/backendApiGeneric'
import { listMailTemplates, type MailTemplate, type MailTemplateCategory } from '@/services/mailTemplatesApi'
import { useAuthStore } from '@/stores/authStore'
import { useSnackbarStore } from '@/stores/snackbarStore'

export function useMailTemplates () {
  const list = ref<MailTemplate[]>([])
  const categories = ref<MailTemplateCategory[]>([])
  const canManageShared = ref(false)
  const companies = ref<Array<{ id: number, name: string }>>([])
  const loading = ref(false)
  let loaded = false

  async function loadCompanies (): Promise<void> {
    if (!canManageShared.value || companies.value.length > 0) {
      return
    }
    try {
      const store = useAuthStore()
      const data = await getRequestGeneric(`/protected/companies/load-companies?idOrganization=${store.idOrganization}`)
      companies.value = (Array.isArray(data) ? data : [])
        .map((c: { id?: unknown, name?: unknown }) => ({ id: Number(c.id), name: String(c.name || `Unternehmen ${c.id}`) }))
        .filter(c => c.id)
    } catch {
      // Unternehmensauswahl bleibt leer – Vorlagen lassen sich trotzdem anlegen.
    }
  }

  async function load (force = false): Promise<void> {
    if (loaded && !force) {
      return
    }
    loading.value = true
    try {
      const res = await listMailTemplates()
      list.value = res.templates
      categories.value = res.categories
      canManageShared.value = res.canManageShared
      loaded = true
      await loadCompanies()
    } catch (error) {
      useSnackbarStore().showSnackbar({
        message: (error as Error)?.message || 'Vorlagen konnten nicht geladen werden',
        type: 'error',
      })
    } finally {
      loading.value = false
    }
  }

  /** Nach der Rückkehr aus der Vorlagenverwaltung frisch laden. */
  function invalidate () {
    loaded = false
  }

  /** Neu gespeicherte Vorlage sofort in der Liste zeigen. */
  function remember (saved: MailTemplate) {
    list.value = [...list.value.filter(t => t.id !== saved.id), saved]
  }

  return { list, categories, canManageShared, companies, loading, load, invalidate, remember }
}
